<template>
  <div class="setting-mobile">
    <!-- Home: profile + grouped section list -->
    <div v-show="!active" class="setting-mobile__home">
      <button type="button" class="setting-mobile__profile" @click="open('business')">
        <v-avatar size="56" color="primary lighten-5">
          <v-img v-if="form.imagePickerVal" :src="form.imagePickerVal" />
          <v-icon v-else color="primary" large>mdi-storefront-outline</v-icon>
        </v-avatar>
        <div class="setting-mobile__profile-text">
          <div class="setting-mobile__profile-name">{{ form.abrName || $t('abrName') }}</div>
          <div class="setting-mobile__profile-sub">{{ form.mobile || form.email || $t('settingSection_businessHint') }}</div>
        </div>
        <v-icon class="setting-mobile__chevron">mdi-chevron-left</v-icon>
      </button>

      <v-text-field
        v-model="search"
        class="setting-mobile__search"
        :placeholder="$t('settingSearch')"
        prepend-inner-icon="mdi-magnify"
        solo
        flat
        hide-details
        clearable
        dense
      />

      <!-- Search results: toggles are directly editable -->
      <div v-if="searchResults" class="setting-mobile__group">
        <div v-if="!searchResults.length" class="setting-mobile__empty">{{ $t('settingNoResult') }}</div>
        <div v-else class="setting-mobile__list">
          <setting-toggle-row v-for="item in searchResults" :key="item.key" :form="form" :item="item" />
        </div>
      </div>

      <template v-else>
        <div v-for="block in homeBlocks" :key="block.id" class="setting-mobile__group">
          <div class="setting-mobile__group-title">{{ $t(block.title) }}</div>
          <div class="setting-mobile__list">
            <button
              v-for="s in block.items"
              :key="s.id"
              type="button"
              class="setting-mobile__row"
              :disabled="s.disabled"
              @click="open(s.id)"
            >
              <span class="setting-mobile__row-icon" :class="'setting-mobile__row-icon--' + s.tone">
                <v-icon small color="white">{{ s.icon }}</v-icon>
              </span>
              <span class="setting-mobile__row-text">
                <span class="setting-mobile__row-title">{{ $t(s.title) }}</span>
                <span v-if="s.sub" class="setting-mobile__row-sub">{{ s.sub }}</span>
              </span>
              <v-icon small class="setting-mobile__chevron">mdi-chevron-left</v-icon>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Section page: bar (fixed) → body (scrolls) → save (fixed) -->
    <transition name="setting-mobile-slide">
      <div v-show="active" class="setting-mobile__page">
        <header class="setting-mobile__bar">
          <button type="button" class="setting-mobile__back" :aria-label="$t('back')" @click="close">
            <v-icon>mdi-arrow-right</v-icon>
          </button>
          <span v-if="activeMeta" class="setting-mobile__bar-icon" :class="'setting-mobile__row-icon--' + activeMeta.tone">
            <v-icon small color="white">{{ activeMeta.icon }}</v-icon>
          </span>
          <div class="setting-mobile__bar-text">
            <div class="setting-mobile__bar-title">{{ activeMeta ? $t(activeMeta.title) : '' }}</div>
            <div v-if="activeSubtitle" class="setting-mobile__bar-sub">{{ activeSubtitle }}</div>
          </div>
          <v-menu v-if="activeGroup" bottom left offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="setAll(activeGroup, true)">
                <v-list-item-icon class="me-3"><v-icon small>mdi-toggle-switch</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('settingEnableAll') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setAll(activeGroup, false)">
                <v-list-item-icon class="me-3"><v-icon small>mdi-toggle-switch-off-outline</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('settingDisableAll') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </header>

        <div ref="pageBody" class="setting-mobile__body" :class="{ 'setting-mobile__body--fill': activeFill }">
          <!-- Business (kept mounted so the logo picker survives navigation) -->
          <div v-show="active === 'business'" class="setting-mobile__stack">
            <section class="setting-mobile__hero">
              <file-picker
                ref="refFilePicker"
                v-model="form.imagePicker"
                :label="$t('logo')"
                :avatar="true"
                :multiple="false"
                extensions="image/png, image/gif, image/jpeg"
                type-prop="logo"
                :val="form.imagePickerVal ? form.imagePickerVal : null"
              />
              <div class="setting-mobile__hero-hint">{{ $t('settingLogoHint') }}</div>
            </section>

            <section class="setting-mobile__card">
              <div class="setting-mobile__card-title">{{ $t('settingSection_identity') }}</div>
              <div class="setting-mobile__fields">
                <v-text-field
                  v-model="form.abrName"
                  :label="$t('abrName')"
                  :rules="[(v) => !!v || $t('is-required')]"
                  prepend-inner-icon="mdi-store-outline"
                  hide-details="auto"
                  outlined
                  dense
                  clearable
                />
                <autocomplete v-model="form.bussiness" :title="$t('businessType')" type="business" />
              </div>
            </section>

            <section v-for="g in contactGroups" :key="g.id" class="setting-mobile__card">
              <div class="setting-mobile__card-title">{{ $t(g.title) }}</div>
              <div class="setting-mobile__fields">
                <v-text-field
                  v-for="f in g.fields"
                  :key="f.key"
                  v-model="form[f.key]"
                  :label="$t(f.label)"
                  :prepend-inner-icon="f.icon"
                  :inputmode="f.inputmode"
                  :dir="f.ltr ? 'ltr' : null"
                  hide-details
                  outlined
                  dense
                  clearable
                />
                <v-textarea
                  v-if="g.id === 'contact'"
                  v-model="form.address"
                  :label="$t('address')"
                  prepend-inner-icon="mdi-map-marker-outline"
                  rows="2"
                  auto-grow
                  hide-details
                  outlined
                  dense
                  clearable
                />
              </div>
            </section>
          </div>

          <div v-show="active === 'details'" class="setting-mobile__stack">
            <p class="setting-mobile__hint">{{ $t('settingSection_detailsHint') }}</p>
            <section class="setting-mobile__card">
              <div class="setting-mobile__fields">
                <autocomplete
                  v-for="f in detailFields"
                  :key="f.key"
                  v-model="form[f.key]"
                  :title="$t(f.label)"
                  type="alldetail"
                  :value="form[f.key]"
                />
              </div>
            </section>
          </div>

          <div v-for="g in groups" v-show="active === g.id" :key="g.id" class="setting-mobile__stack">
            <div class="setting-mobile__summary">
              <span class="setting-mobile__summary-count">{{ enabledCount(g) }}/{{ g.items.length }}</span>
              <span>{{ $t(g.subtitle) }}</span>
            </div>
            <div class="setting-mobile__list">
              <setting-toggle-row v-for="item in g.items" :key="item.key" :form="form" :item="item" />
            </div>
            <section v-if="g.id === 'invoice'" class="setting-mobile__card">
              <div class="setting-mobile__fields">
                <v-select
                  v-model="form.returnProductInquiryMode"
                  :items="inquiryModes"
                  :label="$t('returnProductInquiryMode')"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  clearable
                  hide-details
                  :menu-props="{ offsetY: true }"
                />
              </div>
            </section>
          </div>

          <div v-if="investor" v-show="active === 'investor'" class="setting-mobile__stack">
            <section class="setting-mobile__card">
              <div class="setting-mobile__fields">
                <autocomplete v-model="form.brokerAccount" :title="$t('brokerAccount')" type="person" :value="form.brokerAccount" />
                <v-text-field v-model="form.brokerPercent" :label="$t('brokerPercent')" suffix="%" inputmode="decimal" hide-details outlined dense clearable />
                <autocomplete v-model="form.defaultCustomer" :title="$t('defaultCustomer')" type="person" :value="form.defaultCustomer" />
              </div>
            </section>
          </div>

          <!-- Self-contained modules: mounted only while open -->
          <template v-for="m in modules">
            <div v-if="active === m.id" :key="m.id" class="setting-mobile__module" :class="{ 'setting-mobile__module--fill': m.fill }">
              <component :is="m.component" />
            </div>
          </template>

          <div v-if="active === 'payment'" class="setting-mobile__payment">
            <div class="setting-mobile__chips">
              <button
                v-for="(p, i) in paymentModules"
                :key="p.id"
                type="button"
                class="setting-mobile__chip"
                :class="{ 'setting-mobile__chip--active': paymentTab === i }"
                @click="paymentTab = i"
              >
                <v-icon small>{{ p.icon }}</v-icon>{{ $t(p.title) }}
              </button>
            </div>
            <div class="setting-mobile__module">
              <component :is="paymentModules[paymentTab].component" :key="paymentTab" />
            </div>
          </div>
        </div>

        <footer v-if="isGeneral" class="setting-mobile__save">
          <v-btn
            class="setting-mobile__save-btn"
            color="primary"
            block
            depressed
            :loading="loading"
            @click="$emit('save')"
          >
            <v-icon left>mdi-check</v-icon>{{ $t('submit') }}
          </v-btn>
        </footer>
      </div>
    </transition>
  </div>
</template>

<script>
import settingViewMixin from './settingViewMixin'

const MODULE_IDS = ['accounting', 'invoicePrint', 'payment']
// Section is kept in the URL (?s=<id>) so the phone's back button closes the
// section instead of leaving the settings page.
const QUERY_KEY = 's'
const TONES = {
  business: 'blue',
  details: 'indigo',
  inventory: 'orange',
  invoice: 'green',
  customers: 'pink',
  branch: 'teal',
  investor: 'purple',
  accounting: 'indigo',
  invoicePrint: 'grey',
  payment: 'green'
}

export default {
  name: 'SettingMobile',
  mixins: [settingViewMixin],
  data: () => ({
    active: null,
    search: '',
    paymentTab: 0,
    pushedSection: false
  }),
  computed: {
    contactGroups () {
      const byKey = Object.fromEntries(this.contactFields.map(f => [f.key, f]))
      const pick = keys => keys.map(k => byKey[k]).filter(Boolean)
      return [
        {
          id: 'contact',
          title: 'settingContactInfo',
          fields: pick(['mobile', 'phone', 'email']).map(f => ({ ...f, ltr: true }))
        },
        {
          id: 'legal',
          title: 'settingSection_legal',
          fields: pick(['nationalCode', 'financialCode', 'postalCode']).map(f => ({ ...f, ltr: true }))
        }
      ]
    },
    homeBlocks () {
      const t = id => TONES[id] || 'blue'
      const general = [
        { id: 'business', icon: 'mdi-storefront-outline', title: 'settingSection_business', sub: this.form.abrName, subtitle: 'settingSection_businessHint' },
        { id: 'details', icon: 'mdi-book-open-variant', title: 'settingSection_details' }
      ]
      const rules = this.groups.map(g => ({
        id: g.id,
        icon: g.icon,
        title: g.title,
        sub: this.$t('settingEnabledCount', { n: this.enabledCount(g), total: g.items.length })
      }))
      if (this.investor) {
        rules.push({ id: 'investor', icon: 'mdi-handshake-outline', title: 'settingSection_investor', subtitle: 'settingSection_investorHint' })
      }
      const more = [
        ...this.modules.map(m => ({ id: m.id, icon: m.icon, title: m.title })),
        {
          id: 'payment',
          icon: 'mdi-credit-card-outline',
          title: 'paymentSetting',
          disabled: !this.gateway,
          sub: this.gateway ? null : this.$t('settingGatewayInactive')
        }
      ]
      return [
        { id: 'general', title: 'settingSection_business', items: general },
        { id: 'rules', title: 'settingSection_rules', items: rules },
        { id: 'more', title: 'settingSection_more', items: more }
      ].map(b => ({ ...b, items: b.items.map(s => ({ ...s, tone: t(s.id) })) }))
    },
    searchResults () {
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return null
      const all = [].concat(...this.groups.map(g => g.items))
      return all.filter(i => this.$t(i.label).toLowerCase().includes(q))
    },
    activeGroup () {
      return this.groups.find(g => g.id === this.active) || null
    },
    activeMeta () {
      if (!this.active) return null
      for (const b of this.homeBlocks) {
        const s = b.items.find(i => i.id === this.active)
        if (s) return s
      }
      return null
    },
    activeSubtitle () {
      const m = this.activeMeta
      if (!m) return ''
      if (m.subtitle) return this.$t(m.subtitle)
      if (this.activeGroup) return m.sub
      return ''
    },
    activeFill () {
      if (this.active === 'payment') return true
      const m = this.modules.find(x => x.id === this.active)
      return !!(m && m.fill)
    },
    isGeneral () {
      return !!this.active && !MODULE_IDS.includes(this.active)
    }
  },
  watch: {
    '$route.query.s': {
      immediate: true,
      handler (id) {
        const valid = id && this.homeBlocks.some(b => b.items.some(s => s.id === id && !s.disabled))
        this.active = valid ? id : null
        if (!valid) this.pushedSection = false
        this.$nextTick(() => {
          if (this.$refs.pageBody) this.$refs.pageBody.scrollTop = 0
        })
      }
    }
  },
  methods: {
    open (id) {
      if (this.active === id) return
      this.pushedSection = true
      this.$router.push({ query: { ...this.$route.query, [QUERY_KEY]: id } }).catch(() => {})
    },
    close () {
      if (this.pushedSection) {
        // Pop the entry we pushed, same as the hardware back button.
        this.$router.back()
        return
      }
      // Opened directly via URL: replace instead of leaving the page.
      const query = { ...this.$route.query }
      delete query[QUERY_KEY]
      this.$router.replace({ query }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.setting-mobile {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f3f5f9;
}
.theme--dark .setting-mobile { background: #121212; }

/* ---------- Home ---------- */
.setting-mobile__home {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 12px 16px 24px;
}
.setting-mobile__profile {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-align: start;
  color: inherit;
}
.theme--dark .setting-mobile__profile,
.theme--dark .setting-mobile__list,
.theme--dark .setting-mobile__card,
.theme--dark .setting-mobile__hero {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.1);
}
.setting-mobile__profile-text { flex: 1 1 auto; min-width: 0; }
.setting-mobile__profile-name {
  font-size: 1.05rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setting-mobile__profile-sub {
  font-size: 0.8rem;
  opacity: 0.6;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setting-mobile__search { margin: 14px 0 4px !important; }
.setting-mobile__search ::v-deep .v-input__slot {
  background: rgba(0, 0, 0, 0.05) !important;
  border-radius: 12px !important;
}
.setting-mobile__group { margin-top: 18px; }
.setting-mobile__group-title {
  font-size: 0.75rem;
  font-weight: 700;
  opacity: 0.55;
  padding: 0 6px 8px;
}
.setting-mobile__list {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
}
.setting-mobile__list > * + * { border-top: 1px solid rgba(0, 0, 0, 0.06); }
.theme--dark .setting-mobile__list > * + * { border-top-color: rgba(255, 255, 255, 0.08); }
.setting-mobile__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 60px;
  padding: 8px 14px;
  text-align: start;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
}
.setting-mobile__row:active:not(:disabled) { background: rgba(0, 0, 0, 0.04); }
.setting-mobile__row:disabled { opacity: 0.45; }
.setting-mobile__row-icon,
.setting-mobile__bar-icon {
  flex: 0 0 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.setting-mobile__row-icon--blue { background: #1e88e5; }
.setting-mobile__row-icon--indigo { background: #3949ab; }
.setting-mobile__row-icon--orange { background: #fb8c00; }
.setting-mobile__row-icon--green { background: #43a047; }
.setting-mobile__row-icon--pink { background: #d81b60; }
.setting-mobile__row-icon--teal { background: #00897b; }
.setting-mobile__row-icon--purple { background: #8e24aa; }
.setting-mobile__row-icon--grey { background: #757575; }
.setting-mobile__row-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.setting-mobile__row-title { font-size: 0.9rem; font-weight: 500; }
.setting-mobile__row-sub {
  font-size: 0.75rem;
  opacity: 0.55;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setting-mobile__chevron { opacity: 0.35; flex: 0 0 auto; }
.setting-mobile__empty {
  padding: 32px 16px;
  text-align: center;
  opacity: 0.55;
  font-size: 0.85rem;
}

/* ---------- Section page ---------- */
.setting-mobile__page {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.setting-mobile__bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 60px;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.theme--dark .setting-mobile__bar { background: #1e1e1e; border-bottom-color: rgba(255, 255, 255, 0.1); }
.setting-mobile__back {
  flex: 0 0 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  color: inherit;
  -webkit-tap-highlight-color: transparent;
}
.setting-mobile__back:active { background: rgba(0, 0, 0, 0.1); }
.setting-mobile__bar-text { flex: 1 1 auto; min-width: 0; }
.setting-mobile__bar-title {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setting-mobile__bar-sub {
  font-size: 0.74rem;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setting-mobile__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 16px;
  /* flex column so modules can stretch and push their actions to the bottom */
  display: flex;
  flex-direction: column;
}
.setting-mobile__body > * {
  flex-shrink: 0;
}
.setting-mobile__stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.setting-mobile__hero {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start; /* start = right in RTL */
  gap: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
}
.setting-mobile__hero ::v-deep > *:first-child { flex: 0 0 auto; max-width: 96px; }
.setting-mobile__hero-hint { flex: 1 1 auto; font-size: 0.78rem; line-height: 1.7; opacity: 0.6; text-align: start; }
.setting-mobile__card {
  padding: 14px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
}
.setting-mobile__card-title {
  font-size: 0.78rem;
  font-weight: 700;
  opacity: 0.6;
  margin: 0 2px 16px;
}
.setting-mobile__fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 22px;
}
.setting-mobile__fields > * { margin: 0 !important; padding: 0 !important; min-width: 0; }
.setting-mobile__fields ::v-deep .v-input { height: auto !important; }
.setting-mobile__fields ::v-deep .v-input__slot { margin-bottom: 0 !important; border-radius: 8px !important; }
.setting-mobile__hint {
  font-size: 0.8rem;
  opacity: 0.6;
  margin: 0 4px;
  line-height: 1.7;
}
.setting-mobile__summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
  font-size: 0.8rem;
  line-height: 1.6;
  opacity: 0.75;
}
.setting-mobile__summary-count {
  flex: 0 0 auto;
  padding: 2px 10px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.75rem;
  direction: ltr;
  background: rgba(25, 118, 210, 0.1);
  color: var(--v-primary-base, #1976d2);
}

/* Modules (accounting / invoice print / gateways) */
.setting-mobile__body--fill {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.setting-mobile__module--fill {
  flex: 1 1 auto;
  min-height: 0;
  margin: 0 !important;
  background: #fff;
}
.theme--dark .setting-mobile__module--fill { background: #1e1e1e; }
.setting-mobile__module:not(.setting-mobile__module--fill) {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  margin: 0 -12px;
}
.setting-mobile__module:not(.setting-mobile__module--fill) ::v-deep > * {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}
.setting-mobile__module ::v-deep > * > .v-form {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}
.setting-mobile__module ::v-deep .v-form > .v-card__actions {
  position: sticky;
  bottom: -16px;
  z-index: 3;
  margin: auto 0 -16px !important;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom)) !important;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.theme--dark .setting-mobile__module ::v-deep .v-form > .v-card__actions { background: #1e1e1e; }
.setting-mobile__payment {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.setting-mobile__payment .setting-mobile__chips {
  flex: 0 0 auto;
  padding: 12px 12px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.theme--dark .setting-mobile__payment .setting-mobile__chips { background: #1e1e1e; }
.setting-mobile__payment > .setting-mobile__module {
  flex: 1 1 auto !important;
  min-height: 0;
  margin: 0 !important;
}
/* Gateway components own their scroll + footer: let them shrink to fit. */
.setting-mobile__payment > .setting-mobile__module ::v-deep > *,
.setting-mobile__payment > .setting-mobile__module ::v-deep > * > .v-form {
  flex: 1 1 auto !important;
  min-height: 0;
}
.setting-mobile__chips {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 12px;
  scrollbar-width: none;
}
.setting-mobile__chips::-webkit-scrollbar { display: none; }
.setting-mobile__chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 18px;
  font-size: 0.82rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  color: inherit;
}
.setting-mobile__chip--active {
  background: var(--v-primary-base, #1976d2);
  border-color: var(--v-primary-base, #1976d2);
  color: #fff;
}
.setting-mobile__chip--active .v-icon { color: #fff !important; }

/* Save bar */
.setting-mobile__save {
  flex: 0 0 auto;
  padding: 10px 16px calc(12px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 -6px 16px rgba(15, 23, 42, 0.06);
}
.theme--dark .setting-mobile__save { background: #1e1e1e; border-top-color: rgba(255, 255, 255, 0.1); }
.setting-mobile__save-btn {
  height: 50px !important;
  border-radius: 14px !important;
  font-size: 0.95rem !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.28) !important;
}

.setting-mobile-slide-enter-active { transition: transform 0.22s ease, opacity 0.22s ease; }
.setting-mobile-slide-enter { transform: translateX(-24px); opacity: 0; }
</style>
